"use client";

import * as React from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/cn";
import { useTheme, type Semantics } from "./theme-provider";

const PALETTE_LABELS: Record<Semantics, string> = {
  "rose-pine": "Rosé Pine",
  catppuccin: "Catppuccin",
};

// The four semantic roles most visibly differ between palettes — a compact "what you'll get" preview.
const SWATCH_TOKENS = ["--destructive", "--warning", "--success", "--info"] as const;

export interface PaletteToggleProps extends Omit<React.ComponentProps<typeof Button>, "onClick"> {}

/**
 * A button that cycles the active semantic palette (Rosé Pine ↔ Catppuccin) via `useTheme` (must
 * live inside a `ThemeProvider`). Built on the `Button` primitive — defaults to `variant="outline"
 * size="sm"`, both overridable — sharing its foundation with `ThemeToggle` so the two (usually
 * placed side by side, e.g. a header) inherit height/border/radius/focus-ring from one source
 * instead of two independent style definitions. Shows four live swatches (destructive / warning /
 * success / info) beside the current palette's name, so the control previews what it switches
 * between.
 */
export function PaletteToggle({ variant = "outline", size = "sm", className, ...props }: PaletteToggleProps) {
  const { semantics, toggleSemantics } = useTheme();
  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      aria-label="Switch semantic palette"
      onClick={toggleSemantics}
      data-slot="palette-toggle"
      className={cn(className)}
      {...props}
    >
      <span className="flex items-center gap-0.5" aria-hidden="true">
        {SWATCH_TOKENS.map((token) => (
          <span key={token} className="size-2.5 rounded-full" style={{ backgroundColor: `var(${token})` }} />
        ))}
      </span>
      {PALETTE_LABELS[semantics]}
    </Button>
  );
}
